import styled from "styled-components";
import {
  primaryFont,
  secondaryFont,
  primaryHeadingColor,
  boxShadow,
  maxWidth,
  wMedium,
  wMBold,
  maxTablet,
  maxMobile,
  retina,
  scrollbar
} from "Utils/utilsStyle";
import React, {
  useEffect,
  useRef,
  useState,
  useMemo,
} from 'react';
import { getFirstName } from 'Utils/UtilityFunctions';
import ArrowRightIcon from '../../common/assets/svgIcons/ArrowRightIcon';

const HOVER_COLOR = '#1EC252';
const Wrapper = styled.div`
  z-index: 0;
  box-shadow: ${boxShadow};
  background:#fff;
  padding: 15px 0 15px 0;
  border-radius: 16px;
  height: auto;
  width: 102%;
  margin-left: -20px;
  margin-top: 25px;
  position: relative;
  @media all and (max-width: ${maxWidth}) , ${retina} {
    min-height: auto;
    padding: 15px 0 15px 0;
    border-radius: 12px;
    width: 101%;
    margin-left: -10px;
    margin-top: 0px;
    @media all and (max-width: ${maxMobile}) {
      padding: 10px 0 10px 0;
    }
  }
  @media all and (max-width: ${maxMobile}) {
    min-height: auto;
    width: 100%;
    margin-left: 0px;
  }

  .timeLine-spacing {
    margin: 63px 0 0 20px;
    @media all and (max-width: ${maxWidth}) , ${retina} {
     margin: 40px 0 0 20px; 
    }
    @media all and (max-width: ${maxMobile}) {
     margin: 10px 0 0 0;  
    }
  }
  .head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 30px;
  }
  .scroll-wrapper {
    max-height: 505px;
    margin-right: 2px;
    ${scrollbar};
    ::-webkit-scrollbar { width: 4px; } 
    ::-webkit-scrollbar { margin-top: -10px; } 
    ::-webkit-scrollbar-thumb { background-color: #DCDCDD; }
    ::-webkit-scrollbar-track { background-color: transparent; } 
    border-radius: 0px !important;
    @media all and (max-width: ${maxWidth}), ${retina} {
      max-height: 334px;
    }
    @media all and (min-height: ${maxTablet}) {
      max-height: 505px;
    }
    .infinite-scroll-component__outerdiv {
      height: calc(100% - 42px);
      .infinite-scroll-component {
        height: 100% !important;
        min-height: 505px;
        overflow: hidden !important;
        @media all and (max-width: ${maxWidth}), ${retina} {
          min-height: 334px;
        }
        @media all and (min-height: ${maxTablet}) {
          min-height: 505px;
        }
      }
    }
  }
  .items-wrapper {
    padding: 0 35px 0 40px;
    @media all and (max-width: ${maxMobile}) {
      padding: 0 15px 0 15px;
    }
  }
`;

const Heading = styled.h2`
  font-size: 24px;
  font-weight: ${wMBold};
  font-family: ${secondaryFont};
  letter-spacing: -0.66px;
  color: ${primaryHeadingColor};
  margin-bottom: 0px;
  @media all and (max-width: ${maxTablet}){
    font-size: 16px;
  }
`;

const FAQItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 18px 0;
  gap: 12px;
  cursor: pointer;
  @media all and (max-width: ${maxMobile}) {
    align-items: flex-start;
    padding: 12px 0;
  }
`;

const QuestionWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  @media all and (max-width: ${maxMobile}) {
    align-items: flex-start;
  }
`;

const QuestionNumber = styled.span`
  color: #BFBFBF;
  font-family: ${secondaryFont};
  font-weight: ${wMBold};
  font-size: 18px;
  color: #BFBFBF;
  flex-shrink: 0;
  width: 15px;
  @media all and (max-width: ${maxMobile}) {
    font-size: 13px;
  }
`;

const QuestionText = styled.span<{ isHovered?: boolean }>`
  font-family: ${primaryFont};
  font-weight: ${wMedium};
  font-size: 16px;
  font-style: normal;
  line-height: 18px;
  letter-spacing: -0.375px;
  color: ${({ isHovered }) => (isHovered ? HOVER_COLOR : '#424B50')};
  @media all and (max-width: ${maxMobile}) {
    font-size: 13px;
  }
`;

const IconWrapper = styled.span`
  font-size: 14px;
  flex-shrink: 0;
  @media all and (max-width: ${maxMobile}) {
    font-size: 12px;
  }
`;

type Props = {
  faqs: string[];
  onFaqClick?: (faq: string, faqIdx: number) => void;
  name?: string
};

const FAQs = (props: Props) => {
  const { faqs = [], onFaqClick, name } = props;
  const firstItemRef = useRef<HTMLDivElement | null>(null);
  const [hoveredFaqIdx, setHoveredFaqIdx] = useState<number | null>(null);
  const [listStyle, setListStyle] = useState<React.CSSProperties | undefined>(undefined);

  // compute max-height when there are more than 10 items so the list shows 10 items and becomes scrollable
  useEffect(() => {
    if (faqs.length > 10 && firstItemRef.current) {
      const itemEl = firstItemRef.current;
      const itemHeight = itemEl.offsetHeight;
      const style = window.getComputedStyle(itemEl);
      const marginTop = parseFloat(style.marginTop || '0') || 0;
      const marginBottom = parseFloat(style.marginBottom || '0') || 0;
      const totalPerItem = itemHeight + marginTop + marginBottom;

      setListStyle({
        maxHeight: `${totalPerItem * 10}px`,
        overflowY: 'auto',
      });
    } else {
      setListStyle({
        maxHeight: 'none',
        overflowY: 'visible',
      });
    }
  }, [faqs]);

  // Converts spans to <strong> without the leading '@' and preserves text nodes.
  const htmlToReactNodes = (htmlString: string): React.ReactNode => {
    if (!htmlString) return null;
    try {
      const parser = new DOMParser();
      const doc = parser.parseFromString(htmlString, 'text/html');

      const nodes: React.ReactNode[] = [];
      Array.from(doc.body.childNodes).forEach((node, idx) => {
        if (node.nodeType === Node.TEXT_NODE) {
          nodes.push(node.textContent || '');
          return;
        }
        if (node.nodeType === Node.ELEMENT_NODE) {
          const el = node as Element;
          if (el.tagName.toLowerCase() === 'span') {
            const txt = (el.textContent || '').replace(/^@/, '');
            nodes.push(<span key={idx}>{txt}</span>);
            return;
          }
          nodes.push(el.textContent || '');
        }
      });
      return nodes.length === 1 ? nodes[0] : <>{nodes}</>;
    } catch (e) {
      return htmlString.replace(/<[^>]*>/g, '').replace(/@/g, '');
    }
  };

  const getFirstName = (fullName: string): string => {
    if (!fullName || typeof fullName !== "string") return "";
    const parts = fullName.trim().split(/\s+/);
    return parts[0];
  }

  const handleFAQClick = (faq: string, faqIdx: number) => {
    if (onFaqClick) onFaqClick(faq, faqIdx);
  };

  const faqNodes = useMemo(() => (faqs || []).map((f) => htmlToReactNodes(f)), [faqs]);

  return (
    <section>
      <div className="head mt-c-50 left-side">
        <Heading>Next steps with {getFirstName(name || '')}</Heading>
      </div>
      <Wrapper>
        <div className="scroll-wrapper" style={listStyle}>
          <div className="items-wrapper">
            {(faqs && faqs.length ? faqs : []).map((faq: string, idx: number) => {
              const isHovered = hoveredFaqIdx === idx;
              return (
                <FAQItem
                  key={idx}
                  ref={idx === 0 ? firstItemRef : undefined}
                  role="button"
                  tabIndex={0}
                  onClick={() => handleFAQClick(faq, idx)}
                  onKeyDown={(e) => { if (e.key === 'Enter') handleFAQClick(faq, idx); }}
                  onMouseEnter={() => setHoveredFaqIdx(idx)}
                  onMouseLeave={() => setHoveredFaqIdx(null)}
                >
                  <QuestionWrapper>
                    <QuestionNumber>{idx + 1}</QuestionNumber>
                    <QuestionText isHovered={isHovered}>{faqNodes[idx]}</QuestionText>
                  </QuestionWrapper>
                  <IconWrapper>
                    <ArrowRightIcon color={isHovered ? HOVER_COLOR : '#BFBFBF'} />
                  </IconWrapper>
                </FAQItem>
              );
            })}
          </div>
        </div>
      </Wrapper>
    </section>
  );
};

export default FAQs;
